import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Store, MapPin, Phone, Mail, Plus,
  Trash2, Loader2, UserCog, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  if (imagePath.startsWith("/uploads/")) return `${BACKEND_URL}/api${imagePath}`;
  return `${BACKEND_URL}${imagePath}`;
};

const InfoRow = ({ icon: Icon, label, value }) => (
  value ? (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[#0A4CAE] mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  ) : null
);

const StoreDetailsPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [store, setStore] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` },
  });

  const fetchStore = async () => {
    try {
      const [storesRes, adminsRes] = await Promise.all([
        axios.get(`${API}/stores`, authHeaders()),
        axios.get(`${API}/admin/stores/${storeId}/admins`, authHeaders()),
      ]);
      const found = storesRes.data.find((s) => s.id === storeId);
      if (!found) { toast.error("Store not found"); navigate("/admin/stores"); return; }
      setStore(found);
      setAdmins(adminsRes.data);
    } catch {
      toast.error("Failed to load store details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStore(); }, [storeId]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/stores/${storeId}/admins`, formData, authHeaders());
      setAdmins((prev) => [res.data, ...prev]);
      toast.success(`${res.data.name} added as store admin`);
      setAddDialogOpen(false);
      setFormData({ name: "", email: "", phone: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add store admin");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAdmin = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/admin/stores/${storeId}/admins/${selectedAdmin.id}`, authHeaders());
      setAdmins((prev) => prev.filter((a) => a.id !== selectedAdmin.id));
      toast.success("Store admin removed");
      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
    } catch {
      toast.error("Failed to remove store admin");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#0A4CAE]" size={32} />
      </div>
    );
  }

  if (!store) return null;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate("/admin/stores")}
        className="flex items-center gap-2 text-gray-500 hover:text-[#0A4CAE] transition-colors text-sm font-medium mb-8"
      >
        <ArrowLeft size={18} /> Back to Stores
      </button>

      {/* Store Info Card */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#e8eef9] rounded-xl flex-shrink-0">
              <Store size={26} className="text-[#0A4CAE]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{store.name}</h1>
              <p className="text-sm text-gray-400 mt-0.5">Store ID: {store.id}</p>
            </div>
          </div>
          {store.latitude && store.longitude && (
            <a
              href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#0A4CAE] bg-[#e8eef9] hover:bg-[#0A4CAE] hover:text-white px-4 py-2 rounded-lg transition-colors self-start"
            >
              <ExternalLink size={15} /> Open in Maps
            </a>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InfoRow icon={Mail} label="Email" value={store.email} />
          <InfoRow icon={Phone} label="Phone" value={store.phone} />
          <InfoRow icon={MapPin} label="Address" value={store.full_address} />
          {store.latitude && store.longitude && (
            <InfoRow icon={MapPin} label="Coordinates" value={`${store.latitude}, ${store.longitude}`} />
          )}
        </div>
        {store.scanner_image && (
          <div className="mt-6 border-t border-gray-100 pt-6">
            <p className="mb-2 text-xs text-gray-400">Scanner</p>
            <img
              src={getImageUrl(store.scanner_image)}
              alt={`${store.name} scanner`}
              className="h-44 w-44 rounded-xl border border-gray-200 bg-white object-contain p-2"
            />
          </div>
        )}
      </div>

      {/* Store Admins */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCog size={18} className="text-[#0A4CAE]" />
            <h2 className="text-base font-semibold text-gray-800">Store Admins</h2>
            <span className="text-xs bg-[#e8eef9] text-[#0A4CAE] font-semibold px-2 py-0.5 rounded-full">
              {admins.length}
            </span>
          </div>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-[#0A4CAE] hover:bg-[#F7851E] text-white h-9 text-sm"
          >
            <Plus size={16} className="mr-1.5" /> Add Admin
          </Button>
        </div>

        {admins.length === 0 ? (
          <div className="text-center py-14">
            <UserCog size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 text-sm">No store admins yet</p>
            <p className="text-gray-400 text-xs mt-1">Add an admin to manage this store</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700">Name</TableHead>
                  <TableHead className="font-bold text-gray-700">Email</TableHead>
                  <TableHead className="font-bold text-gray-700">Phone</TableHead>
                  <TableHead className="font-bold text-gray-700">Added On</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-[#0A4CAE]/10 flex items-center justify-center text-[#0A4CAE] font-bold text-sm flex-shrink-0">
                          {admin.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">{admin.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-gray-600 text-sm">
                        <Mail size={13} className="text-gray-400" />{admin.email}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-gray-600 text-sm">
                        <Phone size={13} className="text-gray-400" />{admin.phone}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">{fmtDate(admin.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setSelectedAdmin(admin); setDeleteDialogOpen(true); }}
                        className="text-gray-500 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={15} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <UserCog size={20} className="text-[#0A4CAE]" /> Add Store Admin
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-1">
            Adding admin for <span className="font-semibold text-gray-800">{store.name}</span>
          </p>
          <form onSubmit={handleAddAdmin} className="space-y-4 mt-3">
            <div>
              <Label className="text-gray-700 font-medium">Name</Label>
              <Input
                value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required placeholder="Full name" className="mt-1 bg-white border-gray-300"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Email</Label>
              <Input
                type="email" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required placeholder="email@example.com" className="mt-1 bg-white border-gray-300"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">Phone Number</Label>
              <Input
                type="tel" value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required placeholder="+91 98765 43210" className="mt-1 bg-white border-gray-300"
              />
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="border-gray-300 text-gray-700">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#0A4CAE] hover:bg-[#083d8a] text-white">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding…</> : "Add Admin"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remove Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Remove Store Admin</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Remove <span className="font-semibold">{selectedAdmin?.name}</span> as admin of{" "}
            <span className="font-semibold">{store.name}</span>?
          </p>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-gray-300 text-gray-700">
              Cancel
            </Button>
            <Button onClick={handleRemoveAdmin} disabled={submitting} className="bg-red-500 hover:bg-red-600 text-white">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Removing…</> : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDetailsPage;
